import { Modal } from 'antd';
import type { ModalFuncProps } from 'antd/lib/modal';
import { eventChannel } from 'redux-saga';
import { all, call, put, select, take, spawn } from 'redux-saga/effects';

import type { UserProfile } from '@frontend/api';

import { collapseQuoteItem } from 'modules/quotes/quoteItems/data/actions';
import {
  getExpandedQuoteItemId,
  getQuoteItemIdFromComponentId,
} from 'modules/quotes/quoteItems/data/selectors';

import { getQuoteId, getQuoteNumberWithRevision } from 'store/quotes/selectors';
import { getUser } from 'store/session/selectors';

import { compareObjectsDeep } from 'utils/objectUtils';

import { receiveCollaborators, setModalStatus } from './actions';
import {
  getCollaborators,
  getCollaboratorByQuoteItemId,
  getModalStatus,
} from './selectors';
import * as service from './service';
import type { Collaborators } from './types';

export default function* root() {
  yield spawn(cleanup);
}

function* cleanup() {
  const chan = eventChannel((emitter) => {
    const handler = () => {
      emitter(true);
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  });
  yield take(chan);
  // try removing the lock when user closes the page
  const expandedId = yield select(getExpandedQuoteItemId);
  if (expandedId) {
    yield call(removeLock, expandedId);
  }
}

function* fetchCollaborators(quoteId: number) {
  if (quoteId != null) {
    const user: UserProfile | null = yield select(getUser);
    if (user != null) {
      const collaborators = yield call(service.listCollaborators, {
        groupId: user.group.id,
        quoteId,
        userId: user.id,
      });
      yield call(receiveCollaboratorsIfChanged, collaborators);
      return collaborators;
    }
  }
}

function* receiveCollaboratorsIfChanged(nextCollaborators: Collaborators) {
  const collaborators = yield select(getCollaborators);
  const isEqual = compareObjectsDeep(collaborators, nextCollaborators);
  if (!isEqual) {
    yield put(receiveCollaborators(nextCollaborators));
  }
}

export function* acquireLock(quoteItemId: number) {
  let acquireResponse;
  let isCancelled = false;
  const user = yield select(getUser);
  acquireResponse = yield call(service.acquireLock, {
    quoteItemId,
    userId: user.id,
  });
  if (!acquireResponse.acquired) {
    const { isOpened } = yield select(getModalStatus);
    if (isOpened) {
      return { isCancelled: false };
    }
    const collaborator = acquireResponse.collaborators[quoteItemId];
    const title = 'Take Over Quote Item';
    const content = (
      <div>
        {collaborator.fullName} is currently editing the quote item. Do you want
        to take over?
        <br />
        <br />
        They might lose changes they are working on.
      </div>
    );
    const isConfirmed = yield call(confirmLockAcquisition, {
      title,
      content,
    });
    if (isConfirmed) {
      acquireResponse = yield call(service.acquireLock, {
        force: true,
        quoteItemId,
        userId: user.id,
      });
    } else {
      isCancelled = true;
    }
  }
  yield put(receiveCollaborators(acquireResponse.collaborators));
  return { isCancelled };
}

export function* removeLock(quoteItemId: number) {
  const user: UserProfile | null = yield select(getUser);
  if (user === null) return;
  const { collaborators } = yield call(service.removeLock, {
    quoteItemId,
    userId: user.id,
  });
  yield put(receiveCollaborators(collaborators));
}

/**
 * @param [opts.quoteItemIds]
 * @param [opts.componentIds]
 */
export function* areQuoteItemsLocked(opts: {
  quoteItemIds?: number[];
  componentIds?: number[];
}) {
  const quoteId = yield select(getQuoteId);
  let quoteItemIdsFromComponentIds: number[] = [];
  if (opts.componentIds) {
    quoteItemIdsFromComponentIds = yield all(
      opts.componentIds.map((componentId) =>
        select(getQuoteItemIdFromComponentId, componentId),
      ),
    );
  }
  yield call(fetchCollaborators, quoteId);
  const user: UserProfile | null = yield select(getUser);
  const quoteItemIds = quoteItemIdsFromComponentIds.concat(
    opts.quoteItemIds ?? [],
  );
  for (const quoteItemId in quoteItemIds) {
    const collaborator = yield select(
      getCollaboratorByQuoteItemId,
      quoteItemId,
    );
    if (collaborator && collaborator.id !== user.id) {
      yield call(reacquireAfterTakeover, quoteItemId);
      return true;
    }
  }
  return false;
}

function* reacquireAfterTakeover(quoteItemId: number) {
  const { isOpened } = yield select(getModalStatus);
  const quoteNumberWithRevision = yield select(getQuoteNumberWithRevision);
  if (isOpened) return;

  const collaborator = yield select(getCollaboratorByQuoteItemId, quoteItemId);
  yield call(Modal.warning, {
    title: 'No Longer Editing',
    content: `${collaborator.fullName} has taken over this quote item.`,
  });

  yield put(
    collapseQuoteItem.request({
      quoteItemId,
      next: `/quotes/edit/${quoteNumberWithRevision}`,
    }),
  );
}

function* confirmLockAcquisition({
  title,
  content,
  variant,
}: {
  title: string;
  content: React.ReactNode;
  variant?: ModalFuncProps['okType'];
}) {
  yield put(setModalStatus({ isOpened: true }));
  const confirmChannel = eventChannel((emitter) => {
    Modal.confirm({
      title,
      content,
      okType: variant,
      okText: 'Take Over',
      cancelText: 'Cancel',
      onOk: () => {
        emitter(true);
      },
      onCancel: () => {
        emitter(false);
      },
    });
    return () => 0;
  });
  const isConfirmed = yield take(confirmChannel);
  yield put(setModalStatus({ isOpened: false }));
  return isConfirmed;
}
